/**
 * Get the base URL for App Builder actions
 */
export function getActionUrl(actionName) {
  const actions = require('../config.json');

  if (!actions[actionName]) {
    return process.env.COMMERCE_BASE_URL + '/rest/V1/' + actionName
  }

  return actions[actionName];
}

/**
 * Call an App Builder action
 * @param {string} actionName - Name of the action to call
 * @param {object} params - Parameters to pass to the action
 * @param token
 */
export async function callAction(actionName, params = {}, token = null) {
  let url = getActionUrl(actionName);
  const {
    ims,
    additionalHeaders = {},
    requestData = {},
    body,
    rawResponse = false
  } = params;

  const headers = {
    'Content-Type': 'application/json',
    'x-gw-ims-org-id': ims?.org || '',
    'authorization': `Bearer ${ims?.token || ''}`,
    ...additionalHeaders,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const method = (requestData?.method || 'GET').toUpperCase();

  if (params.params) {
    const urlParams = new URLSearchParams(params.params);
    url = `${url}${url.includes('?') ? '&' : '?'}${urlParams.toString()}`;
  }

  const options = { method, headers };

  if ((method === 'POST' || method === 'PUT') && body) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(url, options);

  if (rawResponse) {
    return response;
  }

  const rawText = await response.text();

  if (!response.ok) {
    let errorData = {};
    try {
      errorData = JSON.parse(rawText);
    } catch {
      errorData = { message: rawText };
    }
    return { success: false, status: response.status, ...errorData };
  }

  try {
    return JSON.parse(rawText);
  } catch {
    if (/^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(rawText)) {
      return { success: true, token: rawText };
    }
    throw new Error('invalid JSON response');
  }
}


export async function savePrescription(customerData, prescriptionData, ims) {
  return await submitPrescription(prescriptionData, customerData.accessToken, ims);
}

/**
 * Get customer prescriptions from NetSuite
 */
export async function getPrescriptions(customerEmail, token = null, ims = null) {
  return await callAction('cpm/prescriptions', {
    ims,
    requestData: { method: 'GET' },
    params: { customerEmail },
    token
  });
}

/**
 * Upload file to S3
 */
export async function uploadFile(file, fileName, customerEmail, ims = null) {
  // 1) Ask an action for a presigned POST (auth via IMS / require-adobe-auth).
  const presign = await callAction('cpm/s3-presigned-upload', {
    ims,
    requestData: { method: 'POST' },
    body: {
      fileName,
      contentType: file.type,
      customerEmail
    }
  });

  if (!presign || presign.success === false || !presign.uploadUrl) {
    return { success: false, message: presign?.message || presign?.error || 'Failed to get upload URL' };
  }

  // 2) POST the raw file straight to S3 (bypasses the ~1MB runtime limit).
  //    The signed policy fields must come first, and the file must be last.
  const form = new FormData();
  Object.entries(presign.fields).forEach(([k, v]) => form.append(k, v));
  form.append('file', file);

  const s3Response = await fetch(presign.uploadUrl, { method: 'POST', body: form });
  if (!(s3Response.status === 204 || s3Response.ok)) {
    return { success: false, message: `File upload to storage failed (${s3Response.status})` };
  }

  // 3) Return the stored file info for the NetSuite save step.
  return {
    success: true,
    file: {
      url: presign.fileUrl,
      name: fileName,
      id: presign.key,
      size: file.size
    }
  };
}


/**
 * Submit prescription to NetSuite
 */
export async function submitPrescription(prescriptionData, token = null, ims = null) {
  return await callAction('cpm/save', {
    ims,
    requestData: { method: 'POST' },
    body: {
      ...prescriptionData,
      token,
    },
  });
}

/**
 * update prescription to NetSuite
 */
export async function updatePrescription(id, prescriptionData, ims = null) {
  const fullParams = { id, ...prescriptionData };
  return await callAction('cpm/update', {
    ims,
    requestData: { method: 'PUT' },
    params: fullParams,
    body: fullParams,
  });
}



/**
 * delete prescription in NetSuite & S3
 */
export async function deletePrescription(prescriptionId, fileUrl = null, token = null, ims = null) {
  return await callAction('cpm/delete', {
    ims,
    requestData: { method: 'DELETE' },
    params: {
      id: prescriptionId,
      fileUrl,
      token
    }
  });
}

/** Generate presigned URL for S3 file
 */


export async function getPresignedUrl(fileUrl, ims) {
  const response = await callAction('cpm/s3-presigned-url', {
    ims,
    requestData: { method: 'POST' },
    body: { fileUrl }
  });

  if (!response || response.success === false || !response.url) {
    throw new Error(response?.message || response?.error || 'Failed to fetch prescription file');
  }

  // Presigned GET URL — the browser fetches directly from S3.
  return { url: response.url };
}

/**
 * Convert file to base64
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
}


